<script lang="ts" setup>
import AppButton from "@/components/AppButton.vue";
import BaseCheckbox from "@/components/BaseCheckbox.vue";
import BaseDialog from "@/components/BaseDialog.vue";
import BaseSearchBar from "@/components/BaseSearchBar.vue";
import BaseSelect from "@/components/BaseSelect.vue";
import BaseSidebarSection from "@/components/BaseSidebarSection.vue";
import BaseSubContent from "@/components/BaseSubContent.vue";
import BaseSwitch from "@/components/BaseSwitch.vue";
import FilterCategories from "@/components/FilterCategories.vue";
import TheSidebar from "@/components/TheSidebar.vue";
import useFiltersDeprecated from "@/composables/filters-deprecated";
import useParams from "@/composables/params";
import useToast from "@/composables/toast";
import { Category } from "@/types/category";
import { router, usePage } from "@inertiajs/vue3";
import { watchDebounced } from "@vueuse/core";
import _ from "lodash";
import { Eraser } from "lucide-vue-next";
import { computed, reactive, ref, watch } from "vue";
import { route } from "ziggy-js";

const page = usePage();

const props = withDefaults(
  defineProps<{
    filters?: object;
    link_skills?: object[];
    categories?: Category[];
    reloadProps?: string | string[];
  }>(),
  {
    reloadProps: "characters",
  },
);

const open = defineModel("open", {
  type: Boolean,
  default: false,
  required: true,
});

const only = computed(() => {
  return _.isArray(props.reloadProps) ? props.reloadProps : [props.reloadProps];
});

watch(
  () => props.filters,
  (value) => {
    Object.assign(filtersLocal, value);
  },
  { deep: true },
);

const params = useParams();

const filtersLocal = reactive<object>({
  fully_awakened: true,
  ...props.filters,
  ...params.value,
});

const state = reactive({
  link_skills: {
    search: "",
  },
  categories: {
    search: "",
    additional: false,
  },
  category_leaders: {
    search: "",
  },
});

const resetFilters = () => {
  useFiltersDeprecated(filtersLocal).reset({
    page: 1,
    fully_awakened: true,
  });
};

const filterUserBox = ref<string>("false");

watch(
  () => filterUserBox.value,
  (value) => {
    if (value) {
      filterByUserBox(value);
    }
  },
);

const filterByUserBox = (value: string) => {
  if (page.props.user) {
    filtersLocal.user_box = value;
  } else {
    useToast({
      title: "Oops!",
      text: "You need to be logged in, in order to filter characters by your box.",
      icon: "error",
    });

    filterUserBox.value = "false";
  }
};

const filterBoxOrder = ref<boolean>(route().params?.order === "box");
const easyBoxOrderDialogOpen = ref<boolean>(false);

watch(
  () => filterBoxOrder.value,
  (value) => {
    return value ? (filtersLocal.order = "box") : delete filtersLocal.order;
  },
);

const updateFilter = (key: string, value: string | number | boolean) => {
  useFiltersDeprecated(filtersLocal).updateArray(key, value);
};

watchDebounced(
  () => filtersLocal,
  (value) => {
    router.visit(route(route().current(), route().params?.user), {
      data: _.omitBy(
        {
          ...value,
          page: 1,
        },
        (value, key) =>
          _.isNil(value) ||
          value === "" ||
          value === "0" ||
          ((value === false || value === "false") && key !== "fully_awakened"),
      ),
      only: only.value,
      preserveState: true,
    });
  },
  { deep: true },
);
</script>

<template>
  <TheSidebar v-model:open="open">
    <template #heading> Filters</template>

    <template #action>
      <Eraser @click="resetFilters" />
    </template>

    <BaseSidebarSection v-if="!route().current('users.box')" :collapsed="false">
      <div class="flex flex-wrap gap-2">
        <BaseSelect
          v-model="filterUserBox"
          :options="{
            false: 'Both',
            true: 'In My Box',
            except: 'Not in My Box',
          }"
          label="My Box"
        />
      </div>

      <div class="flex items-center gap-1">
        <BaseCheckbox v-model:checked="filterBoxOrder"
          >Easy Box Order
        </BaseCheckbox>
        <i-fa6-solid-circle-question
          class="text-xs font-light italic text-green-100 hover:cursor-pointer"
          @click="easyBoxOrderDialogOpen = true"
        />
      </div>
    </BaseSidebarSection>

    <BaseSidebarSection heading="Rarity">
      <div class="flex flex-wrap gap-2">
        <AppButton
          v-for="(rarity, id) in {
            5: 'lr',
            4: 'ur',
            3: 'ssr',
            2: 'sr',
            1: 'r',
            0: 'n',
          }"
          :key="`filter.rarities.${id}`"
          :active="filtersLocal.rarities?.indexOf(id.toString()) > -1"
          class="grow-0 !px-1 !py-0"
          @click="updateFilter('rarities', id)"
        >
          <img
            :src="`${page.props.app.cdn_url}/assets/en/layout/en/image/cha_rare_sm_${rarity}.png`"
            alt=""
            class="-my-3"
          />
        </AppButton>
      </div>
    </BaseSidebarSection>

    <BaseSidebarSection heading="Type">
      <div class="flex flex-wrap gap-2">
        <AppButton
          v-for="type in [0, 1, 2, 3, 4]"
          :key="`filter.types.${type}`"
          :active="filtersLocal.types?.indexOf(type.toString()) > -1"
          class="grow-0 !px-1 !py-0"
          @click="updateFilter('types', type)"
        >
          <img
            :src="`${page.props.app.cdn_url}/assets/en/layout/en/image/cha_type_icon_sup_0${type}.png`"
            alt=""
            class="-my-2 w-12"
          />
        </AppButton>
        <AppButton
          v-for="(awakeningType, id) in {
            1: 'Super',
            2: 'Extreme',
          }"
          :key="`filter.awakening_types.${id}`"
          :active="filtersLocal.awakening_types?.indexOf(id.toString()) > -1"
          @click="updateFilter('awakening_types', id)"
          >{{ awakeningType }}
        </AppButton>
      </div>
    </BaseSidebarSection>

    <BaseSidebarSection heading="General">
      <div class="flex flex-wrap gap-2">
        <AppButton
          v-for="(name, filter) in {
            fully_awakened: 'Fully Awakened',
            eza: 'Extreme Z-Awakened',
            seza: 'Super Extreme Z-Awakened',
            active_skill: 'Active Skill',
            domain: 'Domain',
            standby_skill: 'Standby Skill',
            transformation: 'Transformation',
            giant_form: 'Giant Form/Rage',
            drop_reward: 'Drop Reward',
            exchangeable: 'Treasure Exchangeable',
          }"
          :active="filtersLocal[filter]"
          @click="filtersLocal[filter] = !filtersLocal[filter]"
          >{{ name }}
        </AppButton>
      </div>
    </BaseSidebarSection>
    <BaseSidebarSection heading="Skill Effects (partial)">
      <div class="grid grid-cols-2 gap-2">
        <AppButton
          v-for="(name, filter) in {
            leader_skill: 'Leader Skill',
            super_atk: 'Super Attack',
            passive_skill: 'Passive Skill',
            active_skill: 'Active Skill',
          }"
          :active="filtersLocal.skill_effect_skills?.indexOf(filter) > -1"
          class="w-full"
          @click="updateFilter('skill_effect_skills', filter)"
          >{{ name }}
        </AppButton>
      </div>

      <div class="flex flex-col gap-2">
        <h5
          class="justify-self-center border-b-2 border-green-900 pb-1 text-base font-medium"
        >
          Self
        </h5>
        <div class="flex flex-wrap gap-2">
          <AppButton
            v-for="(name, filter) in {
              'atk,self': 'Raise ATK',
              'def,self': 'Raise DEF',
              'ki,self': 'Raise Ki',
              'atk_up_ki,self': 'Raise ATK (Ki Sphere)',
              'def_up_ki,self': 'Raise DEF (Ki Sphere)',
              'ki_up_ki,self': 'Raise Ki (Ki Sphere)',
              effective: 'Effective vs. All',
              'critical_hit,self': 'Critical Hit',
              additional_attack: 'Additional Attack',
              guaranteed_hit: 'Guaranteed Hit',
              evasion: 'Evasion',
              guard: 'Guard',
              'reduce_dmg,self': 'Damage Reduction',
              absorb: 'Absorption/Nullification',
              'counterattack,self': 'Counterattack',
              change_ki: 'Change Ki Spheres',
              foresee_super_attack: 'Foresee Super Attack',
              survive_ko: 'Survive K.O. Attacks',
              revive: 'Revive',
            }"
            :active="filtersLocal.skill_effects?.indexOf(filter) > -1"
            class="grow-0 !px-2 !py-1 !text-sm !font-medium"
            @click="updateFilter('skill_effects', filter)"
            >{{ name }}
          </AppButton>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <h5
          class="justify-self-center border-b-2 border-green-900 pb-1 text-base font-medium"
        >
          Team
        </h5>
        <div class="flex flex-wrap gap-2">
          <AppButton
            v-for="(name, filter) in {
              'atk,team': 'Raise ATK',
              'def,team': 'Raise DEF',
              hp: 'Raise HP',
              'ki,team': 'Raise Ki',
              'atk_up_ki,team': 'Raise ATK (Ki Sphere)',
              'def_up_ki,team': 'Raise DEF (Ki Sphere)',
              'ki_up_ki,team': 'Raise Ki (Ki Sphere)',
              'boost_type,team': 'Boost Type',
              'boost_category,team': 'Boost Category',
              'critical_hit,team': 'Critical Hit',
              'recover_hp,,raise': 'Recover HP',
              'reduce_dmg,team': 'Damage Reduction',
              debuff_immune: 'Immune to Debuffs',
              giant_form_turns: 'Giant Form Turns',
            }"
            :active="filtersLocal.skill_effects?.indexOf(filter) > -1"
            class="grow-0 !px-2 !py-1 !text-sm !font-medium"
            @click="updateFilter('skill_effects', filter)"
            >{{ name }}
          </AppButton>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <h5
          class="justify-self-center border-b-2 border-green-900 pb-1 text-base font-medium"
        >
          Enemy
        </h5>
        <div class="flex flex-wrap gap-2">
          <AppButton
            v-for="(name, filter) in {
              'atk,enemy': 'Lower ATK',
              'def,enemy': 'Lower DEF',
              stun: 'Stun',
              seal_super_attack: 'Seal Super Attack',
              disable_action: 'Disable Action',
              disable_guard: 'Disable Guard',
            }"
            :active="filtersLocal.skill_effects?.indexOf(filter) > -1"
            class="grow-0 !px-2 !py-1 !text-sm !font-medium"
            @click="updateFilter('skill_effects', filter)"
            >{{ name }}
          </AppButton>
        </div>
      </div>
    </BaseSidebarSection>
    <BaseSidebarSection heading="Super Attack">
      <BaseSelect
        v-model="filtersLocal.sa_multiplier"
        :options="{
          30: 'Low (30%)',
          70: '70%',
          87: 'Huge (87%)',
          110: 'Extreme (110%)',
          125: 'Supreme (125%)',
          155: 'Immense (155%)',
          200: 'Colossal (200%)',
          201: 'Mega-colossal (201%)',
        }"
        label="Multiplier"
        placeholder="Any"
      />

      <BaseSubContent header="Types">
        <div class="flex flex-wrap gap-2">
          <AppButton
            v-for="(name, id) in {
              1: 'Ki Blast',
              2: 'Unarmed',
              4: 'Physical',
              99: 'Other',
            }"
            :key="`filter.sa_categories.${id}`"
            :active="filtersLocal.sa_categories?.indexOf(id) > -1"
            @click="updateFilter('sa_categories', id)"
            >{{ name }}
          </AppButton>
        </div>
      </BaseSubContent>

      <div class="flex flex-wrap gap-2">
        <AppButton
          v-for="(name, filter) in {
            multi_target: 'Multi-target',
            unit: 'Unit Super Attack',
          }"
          :active="filtersLocal.sa?.indexOf(filter) > -1"
          @click="updateFilter('sa', filter)"
          >{{ name }}
        </AppButton>
      </div>
    </BaseSidebarSection>

    <BaseSidebarSection v-if="link_skills" heading="Link Skills">
      <div class="flex gap-4">
        <BaseSwitch
          v-model="filtersLocal.link_skills_partial"
          left="Full"
          right="Partial"
        />

        <BaseSearchBar v-model="state.link_skills.search" />
      </div>
      <div class="flex flex-wrap gap-2">
        <AppButton
          v-for="linkSkill in link_skills.filter(
            (e) =>
              e.name
                .toLowerCase()
                .indexOf(state.link_skills.search.toLowerCase()) > -1,
          )"
          :key="`filter.link_skills.${linkSkill.id}`"
          :active="
            filtersLocal.link_skills?.indexOf(linkSkill.id.toString()) > -1
          "
          class="grow-0 !px-2 !py-1 !text-sm !font-medium"
          @click="updateFilter('link_skills', linkSkill.id)"
        >
          {{ linkSkill.name }}
        </AppButton>
      </div>
    </BaseSidebarSection>

    <BaseSidebarSection v-if="categories" heading="Categories">
      <BaseSearchBar v-model="state.categories.search" />

      <div class="flex">
        <AppButton
          :active="!state.categories.additional"
          :class="{
            'border-r-0': state.categories.additional,
          }"
          class="!w-full rounded-r-none"
          @click="state.categories.additional = false"
        >
          Primary
        </AppButton>
        <AppButton
          :active="state.categories.additional"
          :class="{
            'border-l-0': !state.categories.additional,
          }"
          class="!w-full rounded-l-none"
          @click="state.categories.additional = true"
        >
          Additional
        </AppButton>
      </div>

      <div class="flex flex-wrap gap-2">
        <AppButton
          v-for="category in categories.filter(
            (e) =>
              e.name
                .toLowerCase()
                .indexOf(state.categories.search.toLowerCase()) > -1,
          )"
          :key="`filter.categories${state.categories.additional ? '_additional' : ''}.${category.id}`"
          :active="
            !state.categories.additional
              ? filtersLocal.categories?.indexOf(category.id.toString()) > -1
              : filtersLocal.categories_additional?.indexOf(
                  category.id.toString(),
                ) > -1
          "
          class="grow-0 !px-2 !py-1 !text-sm !font-medium"
          @click="
            updateFilter(
              'categories' + (state.categories.additional ? '_additional' : ''),
              category.id,
            )
          "
        >
          {{ category.name }}
        </AppButton>
      </div>
    </BaseSidebarSection>

    <BaseSidebarSection v-if="categories" heading="Category Leaders">
      <FilterCategories
        v-model:multiplier-strict="filtersLocal.leader_skill_multiplier_strict"
        v-model:multiplier.number="filtersLocal.leader_skill_multiplier"
        :categories="categories"
        :selected="filtersLocal.category_leaders"
        leader-skill
        @category-selected="(id) => updateFilter('category_leaders', id)"
      />
    </BaseSidebarSection>

    <BaseDialog
      v-model="easyBoxOrderDialogOpen"
      description="Simplify adding characters to your box."
      title="Easy Box Order"
    >
      <div class="flex flex-col gap-2">
        <p>
          Easy Box Order sorts the character list by
          <span class="font-medium">Character</span>&nbsp;<span
            class="text-sm italic"
            >(ascending)</span
          >, followed by
          <span class="font-medium">Release Date</span>&nbsp;<span
            class="text-sm italic"
            >(descending)</span
          >. Apply the same order in-game to get an identically ordered list.
        </p>

        <div class="flex items-center gap-2 py-2 text-sm">
          <div class="grow border-t-2 border-green-900/75" />
          example
          <div class="grow border-t-2 border-green-900/75" />
        </div>

        <p>
          The most convenient in-game menu to view your owned characters* is
          <span class="font-semibold">Team</span> >
          <span class="font-semibold">Dokkan Awaken</span>. From there, update
          the <span class="font-medium">Display Order</span> twice**:
        </p>
        <ol class="list-inside list-decimal">
          <li>
            <span class="font-semibold">Character</span>&nbsp;<span
              class="text-sm italic"
              >(Ascending Order)</span
            >
          </li>
          <li>
            <span class="font-semibold">Released</span>&nbsp;<span
              class="text-sm italic"
              >(Descending Order)</span
            >
          </li>
        </ol>
        <ul class="text-xs font-light italic">
          <li>
            * Characters that do not have a Dokkan Awakening are not visible
            from this menu.
          </li>
          <li>
            ** You must exit out of the filter menu entirely after the first
            update, for the changes to take effect.
          </li>
        </ul>
      </div>
    </BaseDialog>
  </TheSidebar>
</template>

<style scoped></style>
